/**
 * Резолв сезона по году из адреса страницы (`/planning/seasons/$year/...`).
 *
 * Экраны бюджета сезона адресуются ГОДОМ, а API — `season_id`. Год человек
 * читает и правит руками, uuid — нет. Логика была списана в двух экранах
 * (плановые цены, бюджетные справочники); вынесена сюда при третьем
 * (техкарты-варианты), чтобы «сезона нет» везде отвечалось одинаково.
 *
 * Почему два запроса: обычный список сезонов урезан окном видимости из
 * настройки компании (2 прошлых / 1 будущий), и сезон вне окна в нём не
 * придёт. Полный список (`showAll`) тянет полсотни сезонов, поэтому за ним
 * идём только если в окне года не нашлось.
 */
import { useQuery } from '@tanstack/react-query';
import { listSeasonCards, type SeasonCard } from '@/lib/planning/api';

export interface SeasonByYear {
  /** Год из URL корректен (2000..2100). */
  validYear: boolean;
  seasonId: string | null;
  card: SeasonCard | undefined;
  /**
   * Сезоны, которые уже загружены попутно — для экранов, которым нужен не
   * только «этот год», но и соседние (мастер наполнения предлагает взять
   * состав из бюджета ПРОШЛОГО сезона). Отдаём то, что и так лежит в кэше,
   * чтобы страница не заводила третий запрос за тем же списком.
   */
  seasons: SeasonCard[];
  isLoading: boolean;
  isError: boolean;
  /** Год валиден, загрузка кончилась, ошибки нет — а сезона такого нет. */
  missing: boolean;
  /**
   * Карточка года есть, но строки сезона ещё нет (`season_id === null`,
   * ADR-060 §S6 — «виртуальный» год без единой записи). Работать с бюджетом
   * такого года нельзя, пока сезон не открыли.
   *
   * До AK-648 это состояние не различалось вовсе: `missing` было false (год-то
   * в списке), `seasonId` — null, и экраны бюджета молча рисовали пустоту.
   */
  notOpened: boolean;
  /** Загрузка кончилась и год чем-то определился: либо сезон, либо один из отказов. */
  resolved: boolean;
}

export function useSeasonByYear(year: number): SeasonByYear {
  const validYear = Number.isFinite(year) && year >= 2000 && year <= 2100;

  const windowed = useQuery({
    queryKey: ['planning', 'season-cards', 'windowed'],
    queryFn: () => listSeasonCards(false),
    enabled: validYear,
    staleTime: 60_000,
  });
  const cardInWindow = windowed.data?.find((s) => s.year === year);
  const needsFullList = validYear && !!windowed.data && !cardInWindow;
  const all = useQuery({
    queryKey: ['planning', 'season-cards', 'all'],
    queryFn: () => listSeasonCards(true),
    enabled: needsFullList,
    staleTime: 60_000,
  });

  const card = cardInWindow ?? all.data?.find((s) => s.year === year);
  // Полный список полнее окна, но приезжает только когда года в окне не нашлось.
  const seasons = all.data ?? windowed.data ?? [];
  const isLoading = windowed.isLoading || (needsFullList && all.isLoading);
  const settled = validYear && !isLoading && !windowed.isError;
  return {
    validYear,
    seasonId: card?.season_id ?? null,
    card,
    seasons,
    isLoading,
    isError: windowed.isError,
    missing: settled && !card,
    notOpened: settled && !!card && !card.season_id,
    resolved: settled,
  };
}
